import React, { useState, useRef } from 'react';
import { Shield, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const EFFECTIVE_DATE = 'March 28, 2026';

// Exported so /privacy (PrivacyTerms.jsx) can render the SAME source-of-truth
// text a new user must scroll through at signup. Keeps the two views forever in sync.
export const LEGAL_EFFECTIVE_DATE = EFFECTIVE_DATE;

const PRIVACY_POLICY = `
PRIVACY POLICY

Effective Date: ${EFFECTIVE_DATE}

ChildCareConnections and ElderCareConnections (collectively referred to as "the Platform," "we," "us," or "our") are operated by ChildCareConnections Inc. and its owners, partners, subsidiaries, and affiliates. This Privacy Policy describes how we collect, use, disclose, and safeguard your personal information when you access or use our website, mobile application, or any related services.

By accessing or using the Platform, you consent to the practices described in this Privacy Policy. If you do not agree, you must not access or use the Platform.

1. INFORMATION WE COLLECT

1.1 Information You Provide Directly
When you create an account, complete a profile, or use our services, we may collect:
- Full name, email address, phone number, and mailing address
- Account type and care segment selection (Child Care or Elder Care)
- Profile information including qualifications, certifications, photos, and biographical details
- Payment and billing information (processed through secure third-party payment processors)
- Communications sent through our messaging system
- Calendar events, bookings, and scheduling data
- Any other information you voluntarily provide

1.2 Information Collected Automatically
When you access the Platform, we automatically collect:
- Device information (type, operating system, browser)
- IP address and approximate geolocation
- Usage data (pages visited, features used, time spent)
- Cookies and similar tracking technologies
- Push notification tokens (if you opt in)

1.3 Information from Third Parties
We may receive information from:
- Google (if you sign in with Google OAuth)
- Payment processors (transaction confirmations)
- Publicly available sources relevant to your profile verification

2. HOW WE USE YOUR INFORMATION

We use collected information to:
- Provide, maintain, and improve the Platform
- Process account registration and profile creation
- Facilitate communication between users (Parents/Family Members, Providers, Job Seekers, and Camps/Programs)
- Send transactional emails (verification, password resets, booking confirmations)
- Send platform announcements and service updates
- Display relevant search results and recommendations
- Process payments and manage subscriptions
- Monitor and enforce compliance with our Terms of Service
- Prevent fraud, abuse, and unauthorized access
- Generate anonymized analytics to improve our services
- Comply with legal obligations

3. HOW WE SHARE YOUR INFORMATION

3.1 With Other Users
Your profile information (name, qualifications, photos, bio, availability, and location) is visible to other Platform users as intended by the service's functionality. You control what information appears on your profile.

3.2 With Service Providers
We share information with trusted third-party service providers who assist us in operating the Platform, including:
- Cloud hosting and database services
- Email delivery services (SendGrid)
- Payment processing (Stripe)
- Map and geolocation services (Google Maps)
- Analytics services

3.3 For Legal Reasons
We may disclose your information if required to do so by law, regulation, legal process, or governmental request, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.

3.4 Business Transfers
In the event of a merger, acquisition, or sale of all or a portion of our assets, your personal information may be transferred as part of that transaction.

4. DATA RETENTION

We retain your personal information for as long as your account is active or as needed to provide services. Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law or for legitimate business purposes (e.g., fraud prevention, dispute resolution).

5. DATA SECURITY

We implement commercially reasonable technical and organizational measures to protect your personal information, including:
- Encryption of data in transit (TLS/SSL)
- Secure password hashing (bcrypt)
- Access controls and authentication mechanisms
- Regular security assessments

However, no method of transmission over the Internet or electronic storage is completely secure. We cannot guarantee absolute security of your data.

6. YOUR RIGHTS AND CHOICES

Depending on your jurisdiction, you may have the right to:
- Access, correct, or delete your personal information
- Object to or restrict certain processing of your data
- Request portability of your data
- Withdraw consent where processing is based on consent
- Lodge a complaint with a data protection authority

To exercise these rights, contact us at privacy@childcareconnections.ca.

7. CHILDREN'S PRIVACY

The Platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If an account is required for a minor (e.g., a babysitter under 18), it must be created and managed by a parent or legal guardian. If we learn that we have collected information from a child under 18 without parental consent, we will take steps to delete that information.

8. COOKIES AND TRACKING

We use cookies and similar technologies strictly to keep you securely signed in (essential session and authentication cookies), to remember your preferences, and to understand how the Platform is used through our own first-party analytics. We do not use third-party advertising or cross-site tracking cookies, and we do not sell your personal information. You can manage or clear cookies through your browser settings, but disabling essential cookies may prevent you from staying logged in or otherwise limit Platform functionality. By creating an account and using the Platform, you consent to our use of cookies as described here.

9. THIRD-PARTY LINKS

The Platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.

10. CHANGES TO THIS POLICY

We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on the Platform and updating the "Effective Date." Your continued use of the Platform after changes constitutes acceptance of the updated policy.

11. CONTACT US

For questions or concerns about this Privacy Policy:
Email: privacy@childcareconnections.ca
`.trim();

const TERMS_OF_SERVICE = `
TERMS OF SERVICE

Effective Date: ${EFFECTIVE_DATE}

PLEASE READ THESE TERMS OF SERVICE ("TERMS") CAREFULLY BEFORE USING THE PLATFORM. BY CREATING AN ACCOUNT OR USING THE PLATFORM, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE, DO NOT USE THE PLATFORM.

1. DEFINITIONS

"Platform" refers to the ChildCareConnections and ElderCareConnections websites, mobile applications, and all related services operated by ChildCareConnections Inc. and its owners, partners, subsidiaries, and affiliates (collectively, "Company," "we," "us," or "our").

"User" refers to any individual who accesses or uses the Platform, including Parents, Family Members, Providers, Job Seekers, and Camps/Programs operators.

"Content" refers to all text, images, data, profiles, reviews, messages, and other materials posted on or transmitted through the Platform.

2. ELIGIBILITY AND ACCOUNT REQUIREMENTS

2.1 Age Requirement
You must be at least eighteen (18) years of age to create an account and use the Platform. In the event that a User under the age of 18 requires a profile (for example, a minor working as a babysitter), the account MUST be created, managed, and supervised by a parent or legal guardian who accepts full responsibility for the minor's use of the Platform and compliance with these Terms.

2.2 Account Accuracy
You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete. You are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.

2.3 One Account Per Person
Each individual may maintain only one account. Creating multiple accounts may result in termination of all accounts.

3. DISCLAIMER OF LIABILITY AND ASSUMPTION OF RISK

3.1 NO LIABILITY FOR USER CONDUCT
CHILDCARECONNECTIONS, ELDERCARECONNECTIONS, THE COMPANY, ITS OWNERS, DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, PARTNERS, SUBSIDIARIES, AND AFFILIATES (COLLECTIVELY, THE "RELEASED PARTIES") SHALL NOT BE LIABLE AND EXPRESSLY DISCLAIM ANY AND ALL RESPONSIBILITY FOR:

(a) Any individual, entity, or organization that misrepresents themselves, their qualifications, credentials, certifications, licenses, experience, background, identity, or any other information on the Platform;

(b) Any individual, entity, or organization that misuses the Platform for fraudulent, deceptive, harmful, abusive, or unlawful purposes;

(c) The conduct, actions, or omissions of any User, whether online or offline, including but not limited to interactions, communications, meetings, transactions, or arrangements facilitated through or initiated via the Platform;

(d) Any loss, damage, injury, harm, or claim arising from or related to the use of the Platform or reliance on any information, content, or representations made by any User on the Platform.

3.2 USER'S RESPONSIBILITY TO VET AND VERIFY
IT IS THE USER'S SOLE AND ULTIMATE RESPONSIBILITY TO INDEPENDENTLY VERIFY, VET, AND CONFIRM THE IDENTITY, QUALIFICATIONS, CREDENTIALS, CERTIFICATIONS, LICENSES, REFERENCES, BACKGROUND, AND SUITABILITY OF ANY PROVIDER, JOB SEEKER, PARENT, FAMILY MEMBER, CAMP, PROGRAM, OR OTHER USER BEFORE ENGAGING IN ANY ARRANGEMENT, TRANSACTION, EMPLOYMENT, OR CARE RELATIONSHIP.

This includes but is not limited to:
- Conducting independent background checks and reference checks
- Verifying professional licenses, certifications, and credentials with the issuing authorities
- Verifying insurance coverage where applicable
- Conducting in-person interviews and facility visits
- Requesting and verifying vulnerable sector checks and police clearances
- Consulting with qualified professionals (legal, medical, social work) as appropriate

3.3 NO ENDORSEMENT OR GUARANTEE
The presence of a User profile, listing, advertisement, or any content on the Platform does not constitute an endorsement, recommendation, guarantee, or warranty by the Released Parties regarding the quality, safety, legality, suitability, or reliability of any User, service, facility, or product. The Platform is a marketplace and directory — we do not employ, supervise, direct, or control any Provider, Job Seeker, or other User.

3.4 ASSUMPTION OF RISK
By using the Platform, you expressly acknowledge and agree that you assume all risks associated with your use of the Platform and any interactions with other Users, including but not limited to risks of bodily injury, emotional distress, property damage, financial loss, or death, whether caused by negligence or otherwise.

4. INDEMNIFICATION

You agree to indemnify, defend, and hold harmless the Released Parties from and against any and all claims, demands, actions, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from or related to:
(a) Your use of the Platform
(b) Your violation of these Terms
(c) Your violation of any rights of any other person or entity
(d) Any Content you post, submit, or transmit through the Platform
(e) Any arrangement, transaction, or relationship entered into through the Platform

5. USER CONDUCT AND PROHIBITED ACTIVITIES

You agree not to:
- Provide false, misleading, or fraudulent information in your profile or communications
- Misrepresent your identity, qualifications, credentials, or affiliations
- Use the Platform for any unlawful, harmful, threatening, abusive, harassing, defamatory, or discriminatory purpose
- Solicit or collect personal information from other Users for purposes unrelated to the Platform's intended use
- Post or transmit any content that infringes intellectual property rights
- Attempt to gain unauthorized access to any portion of the Platform
- Use automated systems (bots, scrapers) to access the Platform without authorization
- Interfere with or disrupt the Platform or its infrastructure
- Circumvent any security features or access controls

Violation of these prohibitions may result in immediate account termination and, where applicable, referral to law enforcement authorities.

6. CONTENT AND INTELLECTUAL PROPERTY

6.1 User Content
You retain ownership of Content you post on the Platform. By posting Content, you grant the Company a non-exclusive, worldwide, royalty-free, transferable license to use, display, reproduce, and distribute your Content in connection with operating and promoting the Platform.

6.2 Platform Content
All Platform content, design, features, and functionality (excluding User Content) are the exclusive property of the Company and are protected by copyright, trademark, and other intellectual property laws.

7. PAYMENTS AND SUBSCRIPTIONS

7.1 Subscription Plans
Certain features of the Platform may require a paid subscription. Pricing, features, and terms of subscription plans are as published on the Platform and may change from time to time.

7.2 Billing
Subscription fees are billed in advance on a recurring basis. You authorize us to charge your payment method on file for all applicable fees.

7.3 Cancellation and Refunds
You may cancel your subscription at any time. Refunds are subject to our refund policy as published on the Platform.

8. LIMITATION OF LIABILITY

TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE RELEASED PARTIES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM OR RELATED TO YOUR USE OF OR INABILITY TO USE THE PLATFORM, REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, STRICT LIABILITY, OR OTHERWISE) AND EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

THE TOTAL AGGREGATE LIABILITY OF THE RELEASED PARTIES FOR ALL CLAIMS ARISING FROM OR RELATED TO THE PLATFORM SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED CANADIAN DOLLARS (CAD $100.00).

9. DISPUTE RESOLUTION

9.1 Governing Law
These Terms shall be governed by and construed in accordance with the laws of the Province of Saskatchewan, Canada, and the federal laws of Canada applicable therein, without regard to conflict of law principles.

9.2 Arbitration
Any dispute arising from or related to these Terms or the Platform shall be resolved by binding arbitration in accordance with the rules of the ADR Institute of Canada, conducted in the Province of Saskatchewan. Judgment on the arbitration award may be entered in any court of competent jurisdiction.

9.3 Class Action Waiver
YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.

10. TERMINATION

We reserve the right to suspend or terminate your account at any time, with or without cause and with or without notice. Upon termination, your right to use the Platform ceases immediately. Provisions of these Terms that by their nature should survive termination shall survive.

11. MODIFICATIONS TO TERMS

We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Platform. Your continued use of the Platform after such changes constitutes acceptance of the modified Terms.

12. SEVERABILITY

If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.

13. ENTIRE AGREEMENT

These Terms, together with our Privacy Policy, constitute the entire agreement between you and the Company regarding your use of the Platform and supersede all prior agreements and understandings.

14. CONTACT US

For questions about these Terms:
Email: legal@childcareconnections.ca
`.trim();

// Same rationale as LEGAL_EFFECTIVE_DATE — reused by /privacy so the public
// legal page always matches the signup modal.
export const PRIVACY_POLICY_TEXT = PRIVACY_POLICY;
export const TERMS_OF_SERVICE_TEXT = TERMS_OF_SERVICE;

export function LegalAgreementModal({ isOpen, onAgree, onCancel, careSegment }) {
  const [activeTab, setActiveTab] = useState('privacy');
  const [hasScrolledPrivacy, setHasScrolledPrivacy] = useState(false);
  const [hasScrolledTerms, setHasScrolledTerms] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const scrollRef = useRef(null);

  if (!isOpen) return null;

  const brandName = careSegment === 'eldercare' ? 'ElderCareConnections' : 'ChildCareConnections';
  const accentColor = careSegment === 'eldercare' ? 'teal' : 'violet';

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const nearBottom = scrollTop + clientHeight >= scrollHeight - 40;
    if (!nearBottom) return;
    if (activeTab === 'privacy') {
      setHasScrolledPrivacy(true);
      // Auto-advance to the Terms of Service so the user keeps scrolling in one motion.
      if (!hasScrolledTerms) {
        setActiveTab('terms');
        requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; });
      }
    } else if (activeTab === 'terms') {
      setHasScrolledTerms(true);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; });
  };

  const canAgree = hasScrolledPrivacy && hasScrolledTerms && agreed;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in" data-testid="legal-agreement-modal">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl bg-${accentColor}-100 flex items-center justify-center`}>
              <Shield className={`w-5 h-5 text-${accentColor}-600`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Fredoka' }}>
                Privacy Policy & Terms of Service
              </h2>
              <p className="text-xs text-slate-400">Please review and accept to create your {brandName} account</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              data-testid="legal-tab-privacy"
              onClick={() => switchTab('privacy')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'privacy'
                  ? `bg-${accentColor}-500 text-white shadow-lg`
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <Shield className="w-3.5 h-3.5" />
              Privacy Policy
              {hasScrolledPrivacy && <span className="ml-1 text-xs">&#10003;</span>}
            </button>
            <button
              data-testid="legal-tab-terms"
              onClick={() => switchTab('terms')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'terms'
                  ? `bg-${accentColor}-500 text-white shadow-lg`
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              Terms of Service
              {hasScrolledTerms && <span className="ml-1 text-xs">&#10003;</span>}
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-4 text-sm text-slate-600 leading-relaxed"
          onScroll={handleScroll}
          style={{ maxHeight: '50vh' }}
          data-testid="legal-content-scroll"
        >
          <pre className="whitespace-pre-wrap font-sans text-[13px]">
            {activeTab === 'privacy' ? PRIVACY_POLICY : TERMS_OF_SERVICE}
          </pre>
          {((activeTab === 'privacy' && !hasScrolledPrivacy) || (activeTab === 'terms' && !hasScrolledTerms)) && (
            <div className="flex items-center justify-center gap-1 text-xs text-slate-400 py-2 animate-bounce">
              <ChevronDown className="w-4 h-4" /> Scroll to read all
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          {!hasScrolledPrivacy || !hasScrolledTerms ? (
            <p className="text-xs text-slate-400 mb-3 text-center">
              Please scroll through {!hasScrolledPrivacy && !hasScrolledTerms ? 'both the Privacy Policy and Terms of Service' : !hasScrolledPrivacy ? 'the Privacy Policy' : 'the Terms of Service'} to continue.
            </p>
          ) : (
            <label className="flex items-start gap-3 mb-3 cursor-pointer" data-testid="legal-agree-checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className={`mt-0.5 rounded border-slate-300 text-${accentColor}-500 focus:ring-${accentColor}-500`}
                data-testid="legal-agree-checkbox"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I confirm that I am at least 18 years of age (or this account is being set up and verified by a parent or legal guardian for a minor), and I have read, understood, and agree to the <strong>Privacy Policy</strong> and <strong>Terms of Service</strong> of {brandName}.
              </span>
            </label>
          )}

          <div className="flex gap-3">
            <Button
              data-testid="legal-cancel-btn"
              onClick={onCancel}
              variant="outline"
              className="flex-1 rounded-xl h-11 text-sm"
            >
              Cancel
            </Button>
            <Button
              data-testid="legal-agree-btn"
              onClick={onAgree}
              disabled={!canAgree}
              className={`flex-1 rounded-xl h-11 text-sm text-white shadow-lg ${
                canAgree
                  ? `bg-${accentColor}-500 hover:bg-${accentColor}-600 shadow-${accentColor}-500/30`
                  : 'bg-slate-300 cursor-not-allowed'
              }`}
            >
              I Agree — Create My Account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
